// DẤU MỐC DỮ LIỆU — "bảng X có đổi gì từ lần bạn xem không?" trả lời bằng ĐÚNG MỘT lượt đọc.
//
// 💸 VÌ SAO (21/09/2026): `/timeline` đọc 526 doc mỗi lượt mở (gardenia), `/bookings` 1.133 — cả hai
// đã có cache IndexedDB (`lib/swr-cache.ts`) nhưng cache kiểu stale-while-revalidate vẫn **luôn tải
// lại** để biết có gì mới, nên không cắt được lượt đọc nào. Đo 7 ngày:
//
//   | bảng                                             | doc/lượt mở | đổi/ngày |
//   | bookings                                         |         358 |     18,6 |
//   | roomBlocks                                       |          60 |        0 |
//   | rooms · roomTypes · roomPricing · pricingRuleSets |         108 |        0 |
//
// Trang mở ~82 lượt/ngày mà dữ liệu chỉ đổi 18,6 lần ⇒ **~77% lượt mở đang đọc lại thứ y hệt**.
//
// Nay mỗi bảng có một con số "lần đổi gần nhất" trong MỘT doc chung. Client đọc 1 doc; trùng với số
// đã lưu ⇒ dùng thẳng cache, **0 lượt đọc tiếp**.
//
// ── VÌ SAO AN TOÀN ĐƯỢC ────────────────────────────────────────────────────────────────────────
// Dấu mốc chỉ đáng tin khi MỌI đường ghi đều đóng dấu. Với `bookings` điều đó khả thi vì đã có lớp
// bọc duy nhất (`lib/booking-write.ts` + `booking-write-server.ts`) và `scripts/scan-producer-writes.mjs`
// canh fail-closed — không còn đường ghi thứ ba. ĐỪNG thêm dấu mốc cho bảng chưa có lớp bọc: sót một
// chỗ ghi nghĩa là người dùng nhìn dữ liệu cũ mà không có lỗi nào phát ra.
//
// ⏱️ VÀ VẪN CÓ HẠN DÙNG: `STAMP_MAX_AGE_MS` buộc tải lại sau 15 phút dù dấu mốc không đổi. Nếu một
// đường ghi nào đó quên đóng dấu thì thiệt hại chặn ở 15 phút chứ không kéo dài vô hạn — cùng lý lẽ
// với TTL của sổ đăng ký tenant (`lib/tenant-db.ts`).
// ⚠️ FILE NÀY PHẢI DÙNG ĐƯỢC CẢ HAI PHÍA nên KHÔNG import gì: `lib/booking-write.ts` là client
// ('use client') còn `booking-write-server.ts` là Admin SDK. Kéo `server-error-logger` vào đây là phá
// ranh giới client/server (xem memory `feedback_client_server_import_boundary`). Mỗi bên tự đóng dấu
// bằng SDK của mình, chỉ dùng chung mấy hằng số dưới đây.

/** Doc chứa mọi dấu mốc của tenant: `tenants/{tid}/system/dataStamp`. */
export const STAMP_DOC = 'dataStamp';

/**
 * Khoá dấu mốc. Mỗi khoá tương ứng ĐÚNG MỘT tập cache (điều kiện #4 của khuôn — xem docs/perf-boot.md).
 *
 * · `bookings`      — bảng booking. An toàn nhờ lớp bọc ghi duy nhất (`lib/booking-write*.ts`).
 * · `timelineStatic` — 5 bảng cấu hình phòng/giá mà khối TĨNH của `/timeline` dùng:
 *   `rooms`, `roomTypes`, `roomBlocks`, `roomPricing`, `pricingRuleSets`.
 *   Chúng KHÔNG có lớp bọc ghi (31 lệnh ghi rải 8 file), nên thay vì dựng 5 lớp bọc thì bắt buộc mọi
 *   nơi ghi phải gọi hàm đóng dấu và để `scripts/scan-producer-writes.mjs` canh — cùng khuôn "luật 2"
 *   đã dùng cho nguồn nuôi việc. Quên gọi ⇒ chốt chặn báo đỏ lúc commit, không phải lúc user kêu.
 *
 * ⚠️ Gộp 5 bảng vào MỘT khoá là cố ý: sửa giá cũng làm mất cache danh sách phòng. Chấp nhận được vì
 * đo 7 ngày cả 5 bảng đổi **0 lần**; nếu sau này một bảng trở nên hay đổi thì phải tách khoá, nếu
 * không nó kéo tụt tỉ lệ trúng của 4 bảng còn lại.
 */
export type StampKey = 'bookings' | 'timelineStatic';

/** Bảng nào thuộc khoá nào — `scripts/scan-producer-writes.mjs` đọc để biết file nào phải đóng dấu. */
export const STAMP_COLLECTIONS: Record<StampKey, readonly string[]> = {
  bookings: ['bookings'],
  timelineStatic: ['rooms', 'roomTypes', 'roomBlocks', 'roomPricing', 'pricingRuleSets'],
};

/**
 * Quá hạn này thì tải lại dù dấu mốc không đổi — chặn thiệt hại khi có đường ghi quên đóng dấu.
 *
 * ⏱️ 4 GIỜ, chọn bằng số đo chứ không bằng cảm giác. Phát lại 451 lượt mở `/timeline` + 73 lượt
 * `/bookings` thật (4 ngày, gardenia) đối chiếu với 92 lần booking đổi trong cùng kỳ:
 *
 *   | hạn cứng | `/timeline` trúng | tiết kiệm/ngày |
 *   | 15 phút  |  58%              | 16.922 |
 *   | 1 giờ    |  71%              | 20.877 |
 *   | 4 giờ    |  80%              | 23.341 |
 *   | 12 giờ   |  81%              | 23.795 |
 *
 * 4 giờ là ĐIỂM GÃY: thêm nữa gần như không lợi (80→81%) mà cửa sổ thiệt hại tăng gấp ba. Bản đầu đặt
 * 15 phút theo cảm tính và nó vứt đi một phần ba lợi ích — khoảng cách giữa hai lần mở `/timeline` có
 * trung vị **6 phút** nhưng đuôi dài, nên cắt ở 15 phút là cắt đúng chỗ đông.
 */
export const STAMP_MAX_AGE_MS = 4 * 60 * 60 * 1000;

/**
 * Field của `bookings` mà **không trang nào dùng dấu mốc này vẽ ra** (`/timeline` + `/bookings`).
 * Patch chỉ gồm những field này ⇒ KHÔNG đóng dấu ⇒ cache của mọi máy còn nguyên.
 *
 * 📏 VÌ SAO (23/09/2026): đo 3 ngày, `timeline-bk` trúng **37%** trong khi `timeline-static` **88%** —
 * cùng trang, cùng lượt mở. Mốc neo (22/09) chữa phần khoá tự đổi nhưng KHÔNG nhúc nhích con số này
 * (38% → 37%), nên nguyên nhân chính là chỗ khác: dấu mốc quá thô. Phân loại 116 lượt ghi booking/3
 * ngày theo hành động thì **chỉ 38% đụng field Timeline thật sự vẽ**; 62% còn lại là `message_sent`
 * (14), sinh hợp đồng HDDH/HDNH (23), trả lời đánh giá (6), dọn giấy tờ Cổng khách (6), in phiếu…
 * — mỗi lượt vẫn xoá sạch cache Timeline của TẤT CẢ máy.
 *
 * ⚠️ CHẶN MẶC ĐỊNH — danh sách này là **miễn trừ**, không phải định nghĩa. Field lạ (mới thêm, hoặc
 * tôi quên) ⇒ **vẫn đóng dấu**. Sai theo hướng này chỉ tốn thêm một lượt tải; sai theo hướng ngược
 * (liệt kê "field nào được vẽ" rồi bỏ sót) là user nhìn dữ liệu CŨ mà không có lỗi nào phát ra —
 * đúng cái bẫy đã ghi ở đầu file. Thêm field vào đây phải kiểm bằng cách grep trang tiêu thụ trước.
 *
 * Khoá dạng `a.b` (`guestPortal.welcomeSentAt`) xét theo đoạn ĐẦU.
 */
export const NON_VIEW_BOOKING_FIELDS: ReadonlySet<string> = new Set([
  // siêu dữ liệu ghi — không hiển thị ở danh sách/timeline
  'history', 'updatedAt', 'updatedBy',
  // Cổng khách: giấy tờ, mã cửa, lượt khách xem
  'guestPortal',
  // hợp đồng đã sinh (xem ở trang chi tiết booking, không ở 2 trang dùng dấu mốc)
  'hddh', 'hdnh',
  // đánh giá: khách đánh giá mình / mình đánh giá khách / mình trả lời
  'guestReviewedAt', 'guestReviewScore', 'guestReviewText', 'guestReviewTextVi', 'guestReviewPublishedAt',
  'guestReviewPrivateFeedback', 'guestReviewPrivateFeedbackVi', 'guestReviewTags', 'guestReviewScores',
  'hostReviewReply', 'hostReviewReplyAt', 'hostReviewReplyByName', 'hostReviewReplyVi', 'hostReviewSkipped',
  'hostGuestReviewBy', 'hostGuestReviewByName', 'hostGuestReviewRecommended', 'hostGuestReviewScores',
  'hostGuestReviewText', 'hostReviewedGuestAt',
  // dấu vết in phiếu
  'paymentRequestPrints', 'rentPaymentRequestPrints',
  // cờ nội bộ cron (review-reconcile/portal-cleanup) — không trang nào render, chỉ bảng việc đọc
  'reviewReconcilePending', 'portalCleanupPending',
]);

/**
 * Patch này có đụng thứ gì hai trang kia vẽ không? Rỗng/không rõ ⇒ `true` (đóng dấu) — xem chặn mặc
 * định ở `NON_VIEW_BOOKING_FIELDS`.
 */
export function bookingPatchTouchesView(patch: Record<string, unknown>): boolean {
  const keys = Object.keys(patch);
  if (keys.length === 0) return true;
  return keys.some(k => !NON_VIEW_BOOKING_FIELDS.has(k.split('.')[0]));
}
